import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SEOHead } from '@/components/SEOHead';

const METABASE_INSTANCE_URL =
  (import.meta.env.VITE_METABASE_INSTANCE_URL as string) || 'https://portal.onpoint.works';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || 'https://api.onpoint.works';

const GridBg = () => (
  <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
);

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'metabase-dashboard': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        token?: string;
        'with-title'?: string | boolean;
        'with-downloads'?: string | boolean;
      };
    }
  }
}

type TokenResponse = { token?: string; expires_in?: number };

type View = 'daily' | 'intraday' | 'v2-command' | 'v2-diagnostics';

const VIEWS: readonly { value: View; label: string }[] = [
  { value: 'daily', label: 'WFM Diagnostics' },
  { value: 'intraday', label: 'Intraday Operations' },
  { value: 'v2-command', label: 'Operations Command Center V2' },
  { value: 'v2-diagnostics', label: 'WFM Diagnostics V2' },
];

export default function Portal() {
  const [embedReady, setEmbedReady] = useState(false);

  const [signedToken, setSignedToken] = useState<string>('');
  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string>('');

  const [view, setView] = useState<View>('daily');

  const refreshTimerRef = useRef<number | null>(null);

  const instanceUrl = useMemo(() => (METABASE_INSTANCE_URL || '').trim().replace(/\/+$/, ''), []);
  const apiBase = useMemo(() => (API_BASE_URL || '').trim().replace(/\/+$/, ''), []);

  // Token fetch + auto-refresh (TTL-based)
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    function clearRefreshTimer() {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }

    async function loadToken(isRefresh = false) {
      if (!isRefresh) {
        setTokenLoading(true);
        setTokenError('');
        setSignedToken('');
      } else {
        setTokenError('');
      }

      try {
        const res = await fetch(`${apiBase}/api/metabase/dashboard-token?view=${view}`, {
          method: 'GET',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`Token endpoint failed (${res.status}): ${text || res.statusText}`);
        }

        const data = (await res.json()) as TokenResponse;
        const tok = (data?.token || '').trim();
        if (!tok) throw new Error('Token endpoint returned empty token');

        const expiresIn = typeof data.expires_in === 'number' ? data.expires_in : 300;

        if (!cancelled) {
          setSignedToken(tok);

          // Refresh: expire olmadan 45sn önce (min 30sn), küçük jitter ile
          clearRefreshTimer();
          const jitter = Math.floor(Math.random() * 8000); // 0-8s
          const refreshInMs = Math.max((expiresIn - 45) * 1000, 30_000) + jitter;
          refreshTimerRef.current = window.setTimeout(() => loadToken(true), refreshInMs);
        }
      } catch (e: any) {
        if (!cancelled) {
          setTokenError(e?.message || 'Failed to load signed token');
        }
      } finally {
        if (!cancelled && !isRefresh) setTokenLoading(false);
      }
    }

    loadToken(false);

    return () => {
      cancelled = true;
      controller.abort();
      clearRefreshTimer();
    };
  }, [apiBase, view]);

  // Metabase embed runtime: load once + stable config
  useEffect(() => {
    if (!signedToken) return;

    (window as any).defineMetabaseConfig = function defineMetabaseConfig(config: any) {
      (window as any).metabaseConfig = config;
    };

    (window as any).defineMetabaseConfig({
      theme: { preset: 'dark' },
      isGuest: true,
      instanceUrl,
    });

    const scriptId = 'metabase-embed-js';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (existing) {
      setEmbedReady(true);
      return;
    }

    const s = document.createElement('script');
    s.id = scriptId;
    s.defer = true;
    s.src = `${instanceUrl}/app/embed.js`;
    s.onload = () => setEmbedReady(true);
    s.onerror = () => setEmbedReady(false);
    document.body.appendChild(s);
  }, [signedToken, instanceUrl]);

  const showLoading = tokenLoading || !embedReady;
  const showError = Boolean(tokenError) || !signedToken;

  const embedHeight = 'calc(100vh - 150px)';
  const minEmbedHeight = 860;

  const handleViewKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const currentIndex = VIEWS.findIndex((v) => v.value === view);
    const nextIndex =
      event.key === 'ArrowRight'
        ? (currentIndex + 1) % VIEWS.length
        : (currentIndex - 1 + VIEWS.length) % VIEWS.length;
    setView(VIEWS[nextIndex].value);
  };

  return (
    <div className="bg-zinc-950">
      <SEOHead
        title="Analytics Portal | OnPoint.Works"
        description="Live preview analytics dashboard portal powered by Metabase."
      />

      <section className="relative section-border overflow-hidden">
        <GridBg />

        {/* HERO */}
        <div className="container-site relative z-10 pt-10 md:pt-12 pb-6 md:pb-8">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
              <span className="text-xs font-mono tracking-widest uppercase text-amber-500">
                WFM Analytics Lab
              </span>

              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-zinc-100 leading-tight">
                    Portal Preview
                  </h1>

                  <p className="mt-3 text-base text-zinc-400 leading-relaxed max-w-2xl">
                    Live preview (demo dataset). In client deployments, each tenant receives an isolated workspace and
                    dashboards mapped to their operational model.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <div className="inline-flex items-center gap-2 rounded-md px-3 py-2 bg-zinc-900/60 border border-zinc-800">
                    <span className="text-[11px] font-mono tracking-widest uppercase text-zinc-400">
                      Dataset
                    </span>
                    <span className="text-xs text-zinc-200">Demo Tenant</span>
                  </div>

                  <div className="inline-flex items-center gap-2 rounded-md px-3 py-2 bg-zinc-900/60 border border-zinc-800">
                    <span className="text-[11px] font-mono tracking-widest uppercase text-zinc-400">
                      Sync
                    </span>
                    <span className="text-xs text-zinc-200">CSV Upload</span>
                  </div>

                  <div className="inline-flex items-center gap-2 rounded-md px-3 py-2 bg-zinc-900/60 border border-zinc-800">
                    <span className="text-[11px] font-mono tracking-widest uppercase text-zinc-400">
                      Runtime
                    </span>
                    <span className="text-xs text-zinc-200">Metabase + Postgres</span>
                  </div>

                  {/* Client Login button (Begin Intake color) */}
                  <a
                    href="https://portal.onpoint.works"
                    className="inline-flex items-center justify-center rounded-md px-4 py-2 text-xs font-medium text-zinc-950 bg-cyan-400 hover:bg-cyan-300 transition-colors"
                  >
                    Client Login
                  </a>
                </div>
              </div>
            </div>

            <div className="h-px w-full bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />
          </div>
        </div>

        {/* EMBED */}
        <div className="relative z-10 pb-12 md:pb-16">
          <div className="mx-auto w-full max-w-[1480px] px-3 sm:px-6">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 overflow-hidden shadow-[0_0_0_1px_rgba(255,255,255,0.02)]">
              {/* Top bar */}
              <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-zinc-800 bg-zinc-950/70">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 font-medium truncate">OnPoint Analytics Portal</div>
                  <div className="text-xs text-zinc-500 truncate">
                    Embedded preview • read-only • demo dataset
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 rounded-md px-3 py-2 bg-zinc-900/60 border border-zinc-800">
                  <span className="h-2 w-2 rounded-full bg-memo-signal" />
                  <span className="text-xs text-zinc-300">Live</span>
                </div>
              </div>

              {/* View tabs */}
              <div
                role="tablist"
                aria-label="Portal view"
                className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-3 border-b border-zinc-800 bg-zinc-950/70"
              >
                <span aria-live="polite" className="sr-only">
                  {(() => {
                    const active = VIEWS.find((v) => v.value === view);
                    return active ? `Showing ${active.label} view` : 'Portal view';
                  })()}
                </span>
                {VIEWS.map((v) => {
                  const isActive = view === v.value;
                  return (
                    <button
                      key={v.value}
                      type="button"
                      role="tab"
                      id={`tab-${v.value}`}
                      aria-selected={isActive}
                      aria-controls="dashboard-panel"
                      onClick={() => setView(v.value)}
                      onKeyDown={handleViewKeyDown}
                      className={[
                        'inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                        isActive
                          ? 'bg-amber-500/15 text-amber-400 border border-amber-500/40'
                          : 'text-zinc-400 border border-transparent hover:text-zinc-200 hover:bg-zinc-900/60',
                      ].join(' ')}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>

              {/* Provenance banner */}
              <div className="border-b border-zinc-800 bg-zinc-950/70 px-4 sm:px-6 py-3">
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500/80" aria-hidden="true" />
                  <span className="text-[11px] font-mono tracking-widest uppercase text-amber-500">
                    Synthetic Showcase / Demo Dataset
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-zinc-400">
                  Generated demo data — not live customer operations
                </div>
              </div>

              {/* Body */}
              <div
                role="tabpanel"
                id="dashboard-panel"
                aria-labelledby={`tab-${view}`}
                className="relative bg-black"
              >
                <div
                  className="w-full"
                  style={{
                    height: embedHeight,
                    minHeight: minEmbedHeight,
                  }}
                >
                  {showLoading ? (
                    <div className="h-full w-full flex items-center justify-center">
                      <div className="text-xs text-zinc-500 font-mono tracking-widest uppercase">
                        Loading Metabase…
                      </div>
                    </div>
                  ) : showError ? (
                    <div className="h-full w-full flex items-center justify-center px-6">
                      <div className="max-w-xl w-full rounded-xl border border-zinc-800 bg-zinc-950/60 p-5">
                        <div className="text-sm text-zinc-100 font-medium">Embed unavailable</div>
                        <div className="mt-2 text-xs text-zinc-400 leading-relaxed">
                          {tokenError || 'Signed token missing.'}
                        </div>
                        <div className="mt-3 text-[11px] text-zinc-500 font-mono">
                          API: {apiBase}
                          <br />
                          Metabase: {instanceUrl}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <metabase-dashboard
                      key={signedToken.slice(0, 16)}
                      token={signedToken}
                      with-title="false"
                      with-downloads="true"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
              
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}